import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, IconButton, Tooltip } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import TicketDetail from "../components/TicketDetail";
import NinjaOneTicketDetail from "../components/NinjaOneTicketDetail";
import { useAppSelector, useSupervisorHook } from "../hooks";
import GlobalLoadingOverlay from "../components/GlobalLoadingOverlay";
import {
  PanelGroup,
  Panel,
  PanelResizeHandle,
  type ImperativePanelHandle,
} from "react-resizable-panels";
import { useUserMsp } from "../hooks/useUserMsp";
import { isNinjaOne, isAutotask } from "../utils/mspUtils";
import { buildTicketAiOverviewContext } from "../utils/ticketAiOverviewContext";
import NeoAssistant from "components/NeoAssistant";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";

/**
 * Supervisor split view: when the ticket pane is fully collapsed, thin rail to
 * reopen conversation + properties (chatbubble/chat panel uses the rest).
 * Theo is the primary workspace; ticket context lives in a smaller pane on the
 * right (or below on compact screens).
 */
const CollapsedTicketPaneRail: React.FC<{
  ticketBelow: boolean;
  onExpand: () => void;
}> = ({ ticketBelow, onExpand }) => (
  <Box
    sx={{
      height: "100%",
      width: "100%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      bgcolor: "#FFFFFF",
      border: "1px solid #E2E8F0",
      borderRadius: "8px",
      minHeight: 0,
      minWidth: 0,
    }}
  >
    <Tooltip title="Show ticket (conversation & properties)">
      <IconButton
        onClick={onExpand}
        size="small"
        aria-label="Show ticket details"
        sx={{
          backgroundColor: "#F1F5F9",
          color: "#475569",
          "&:hover": { backgroundColor: "#E2E8F0" },
        }}
      >
        {ticketBelow ? <KeyboardArrowUpIcon /> : <ChevronLeftIcon />}
      </IconButton>
    </Tooltip>
  </Box>
);

const SupervisorTicketDetail: React.FC = () => {
  const { ticketId } = useParams<{ ticketId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const currentUser = useAppSelector((state: any) => state.auth.user);
  const { selectedTicket, error, handleTicketSelect } =
    useSupervisorHook({}, 1, { skipListFetch: true });
  const theme = useTheme();
  const isCompactTicketLayout = useMediaQuery(theme.breakpoints.down("lg"));
  const ticketCollapseDirection = isCompactTicketLayout ? "below" : "right";
  const ticketPanelRef = useRef<ImperativePanelHandle>(null);
  const [ticketPaneCollapsed, setTicketPaneCollapsed] = useState(false);
  const { userMspInfo } = useUserMsp();
  const msp_id = userMspInfo?.msp_id;
  const returnTo = React.useMemo(() => {
    const params = new URLSearchParams(location.search);
    const value = params.get("returnTo");
    return value && value.startsWith("/supervisor") ? value : null;
  }, [location.search]);
  const prefillPrompt = React.useMemo(() => {
    const params = new URLSearchParams(location.search);
    const value = params.get("prefillPrompt");
    return value && value.trim() ? value : null;
  }, [location.search]);

  // Fetch ticket data when component mounts
  useEffect(() => {
    if (ticketId && userMspInfo?.msp_id && currentUser?.email) {
      handleTicketSelect(ticketId);
    }
  }, [ticketId, userMspInfo?.msp_id, currentUser?.email, handleTicketSelect]);

  const aiOverviewTicketContext = useMemo(
    () => buildTicketAiOverviewContext(selectedTicket),
    [selectedTicket]
  );

  const handleBackToList = () => {
    try {
      if (window.opener && !window.opener.closed) {
        window.opener.focus();
        window.close();
        window.setTimeout(() => {
          if (document.visibilityState === "visible" && returnTo) {
            navigate(returnTo, { replace: true });
          } else if (document.visibilityState === "visible") {
            navigate(-1);
          }
        }, 100);
        return;
      }
    } catch {
      /* ignore cross-window access errors */
    }
    if (returnTo) {
      navigate(returnTo, { replace: true });
      return;
    }
    navigate(-1);
  };

  if (!ticketId || msp_id === undefined || msp_id === null) {
    return <GlobalLoadingOverlay />;
  }

  if (error && !selectedTicket) {
    return <Box sx={{ p: 3, color: "error.main" }}>Error: {error}</Box>;
  }

  const ticketRecord = selectedTicket?.ticket || {};
  const selectedTicketMatchesUrl =
    Boolean(selectedTicket) &&
    [ticketRecord.id, ticketRecord.displayId].some(
      (value) => value != null && String(value) === String(ticketId)
    );
  const ticketForAssistant = selectedTicketMatchesUrl ? selectedTicket : null;
  const assistantTicketRecord = ticketForAssistant?.ticket || {};
  const theoCustomerId = assistantTicketRecord.customer_id || null;
  const theoTicketTitle =
    String(
      assistantTicketRecord.title ||
        assistantTicketRecord.summary ||
        assistantTicketRecord.subject ||
        ""
    ).trim() || `Ticket ${ticketId}`.trim();
  const theoTicketLink =
    typeof window !== "undefined"
      ? `${window.location.origin}/supervisor/tickets/${encodeURIComponent(ticketId || "")}`
      : "";

  return (
    <Box
      sx={{
        display: "flex",
        flexGrow: 1,
        minHeight: 0,
        height: "100%",
        p: 1.5,
        backgroundColor: "#F5F7FB",
      }}
    >
      <Box sx={{ flex: 1, minHeight: 0, width: "100%", display: "flex" }}>
      <PanelGroup direction={isCompactTicketLayout ? "vertical" : "horizontal"}>
        <Panel
          defaultSize={isCompactTicketLayout ? 64 : 58}
          minSize={isCompactTicketLayout ? 40 : 50}
        >
          <div
            style={{
              flex: 1,
              height: "100%",
              width: "100%",
              minWidth: 0,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            <NeoAssistant
              ticketId={ticketId || null}
              ticketDisplayId={
                selectedTicketMatchesUrl
                  ? assistantTicketRecord.displayId || assistantTicketRecord.id || ticketId || ""
                  : ticketId || ""
              }
              userId={currentUser?.email}
              currentUser={currentUser}
              customerId={theoCustomerId}
              ticketTitle={theoTicketTitle}
              ticketLink={theoTicketLink}
              fillAvailableHeight
              aiOverviewTicketContext={
                selectedTicketMatchesUrl ? aiOverviewTicketContext : ""
              }
              ticketPayloadForLifecycle={
                selectedTicketMatchesUrl &&
                assistantTicketRecord &&
                typeof assistantTicketRecord === "object"
                  ? (assistantTicketRecord as Record<string, unknown>)
                  : null
              }
              prefillMessage={prefillPrompt}
            />
          </div>
        </Panel>

        <PanelResizeHandle
          style={{
            width: isCompactTicketLayout ? "100%" : "12px",
            height: isCompactTicketLayout ? "12px" : "100%",
            background: "transparent",
            borderLeft: isCompactTicketLayout ? "none" : "1px solid transparent",
            borderRight: isCompactTicketLayout ? "none" : "1px solid transparent",
          }}
        />

        <Panel
          ref={ticketPanelRef}
          collapsible
          collapsedSize={4}
          defaultSize={isCompactTicketLayout ? 36 : 42}
          minSize={isCompactTicketLayout ? 20 : 22}
          maxSize={isCompactTicketLayout ? 55 : 45}
          onCollapse={() => setTicketPaneCollapsed(true)}
          onExpand={() => setTicketPaneCollapsed(false)}
        >
          {ticketPaneCollapsed ? (
            <CollapsedTicketPaneRail
              ticketBelow={isCompactTicketLayout}
              onExpand={() => ticketPanelRef.current?.expand()}
            />
          ) : userMspInfo?.msp_type && isNinjaOne(userMspInfo.msp_type) ? (
            <NinjaOneTicketDetail
              ticketId={ticketId!}
              onBack={handleBackToList}
              chatPlacement={ticketCollapseDirection}
              compactSidePane={!isCompactTicketLayout}
              onCollapseForChat={() => ticketPanelRef.current?.collapse()}
            />
          ) : userMspInfo?.msp_type && isAutotask(userMspInfo.msp_type) ? (
            <TicketDetail
              ticketId={ticketId!}
              onBack={handleBackToList}
              chatPlacement={ticketCollapseDirection}
              compactSidePane={!isCompactTicketLayout}
              onCollapseForChat={() => ticketPanelRef.current?.collapse()}
            />
          ) : (
            <TicketDetail
              ticketId={ticketId!}
              onBack={handleBackToList}
              chatPlacement={ticketCollapseDirection}
              compactSidePane={!isCompactTicketLayout}
              onCollapseForChat={() => ticketPanelRef.current?.collapse()}
            />
          )}
        </Panel>
      </PanelGroup>
      </Box>
    </Box>
  );
};

export default SupervisorTicketDetail;
